import type { ServerMessage, ClientMessage } from "protocol";

export interface WebSocketCallbacks {
  onOpen: () => void;
  onClose: () => void;
  onError: (error: Event) => void;
  onMessage: (message: ServerMessage) => void;
}

let socket: WebSocket | null = null;
let isConnecting = false;
let currentCallbacks: WebSocketCallbacks | null = null;

export function connect(url: string, callbacks: WebSocketCallbacks): void {
  currentCallbacks = callbacks;

  if (socket && socket.readyState === WebSocket.OPEN) {
    return;
  }

  if (socket && socket.readyState === WebSocket.CONNECTING) {
    return;
  }

  if (isConnecting) {
    return;
  }

  isConnecting = true;
  socket = new WebSocket(url);

  socket.onopen = () => {
    isConnecting = false;
    currentCallbacks?.onOpen();
  };

  socket.onclose = () => {
    isConnecting = false;
    currentCallbacks?.onClose();
    socket = null;
  };

  socket.onerror = (error) => {
    currentCallbacks?.onError(error);
  };

  socket.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      currentCallbacks?.onMessage(message);
    } catch (err) {
      console.error(err);
    }
  };
}

export function disconnect(): void {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.close();
    socket = null;
  }
  // Don't close if still connecting - let it complete
  isConnecting = false;
}

export function send(message: ClientMessage): void {
  if (!socket) {
    console.error("WebSocket not initialized");
    return;
  }
  if (socket.readyState !== WebSocket.OPEN) {
    console.error(`WebSocket not open, state: ${socket.readyState}`);
    return;
  }
  socket.send(JSON.stringify(message));
}

export function isConnected(): boolean {
  return socket !== null && socket.readyState === WebSocket.OPEN;
}
